import * as React from "react";
import { Box, Stack, Typography } from "@mui/material";
import {
  ORBIT_MISSION_S,
  SEPARATION_MISSION_S,
  altitudeKmAt,
  ascentEvents,
  downrangeKmAt,
  flightStageAt,
  formatMissionClock,
  speedKmSAt,
} from "./ascent";

const MONO = "'Roboto Mono', 'Courier New', monospace";

// Drawing: Earth as a big circle whose top shows at the bottom of the
// picture, the target orbit as a ring above it (height exaggerated so it
// can be seen), and the rocket's track climbing from the launch site.
const CX = 300;
const CY = 520;
const EARTH_R = 400;
const ORBIT_R = 470;
const START_DEG = -112;
// Angle covered from liftoff to separation.
const SWEEP_DEG = 36;
// How fast things keep moving along the orbit after separation, and how
// fast the satellite drifts away from the upper stage (degrees per
// mission second).
const ORBIT_DEG_PER_S = 0.012;
const DRIFT_DEG_PER_S = 0.0035;

const STAGE_SEPARATION_S = 155;
const FAIRING_S = 200;
const FALL_S = 420;

function pointAt(radius: number, angleDeg: number) {
  const a = (angleDeg * Math.PI) / 180;
  return { x: CX + radius * Math.cos(a), y: CY + radius * Math.sin(a) };
}

function radiusAt(missionS: number, orbitAltitudeKm: number) {
  const climb = Math.min(1, altitudeKmAt(missionS, orbitAltitudeKm) / orbitAltitudeKm);
  // Most of the height is gained early; later the rocket mostly gains
  // speed along the orbit -- an ease-out curve for the radius.
  return EARTH_R + (ORBIT_R - EARTH_R) * (1 - Math.pow(1 - climb, 2));
}

function angleAt(missionS: number) {
  const powered = START_DEG + SWEEP_DEG * (Math.min(missionS, SEPARATION_MISSION_S) / SEPARATION_MISSION_S);
  return powered + Math.max(0, missionS - SEPARATION_MISSION_S) * ORBIT_DEG_PER_S;
}

function trackPoint(missionS: number, orbitAltitudeKm: number) {
  return pointAt(radiusAt(missionS, orbitAltitudeKm), angleAt(missionS));
}

// A spent part (first stage, fairing half) falling back from where it was
// dropped. Null once it has reached the ground.
function fallingPoint(missionS: number, droppedAt: number, orbitAltitudeKm: number, spreadDeg: number) {
  const t = (missionS - droppedAt) / FALL_S;
  if (t < 0 || t >= 1) return null;
  const startR = radiusAt(droppedAt, orbitAltitudeKm);
  // Keeps rising a little, then drops: a simple ballistic arc.
  const r = startR + (EARTH_R - startR) * t * t + 14 * Math.sin(Math.PI * t) * (1 - t);
  return pointAt(Math.max(EARTH_R, r), angleAt(droppedAt) + spreadDeg * t);
}

// Fixed star field, so it doesn't jump on every render.
const STARS = Array.from({ length: 80 }, (_, i) => ({
  x: (i * 137.5) % 600,
  y: (i * 53.3) % 220,
  r: i % 7 === 0 ? 1.4 : 0.8,
  o: 0.25 + ((i * 29) % 60) / 100,
}));

function Readout(props: { label: string; value: string; highlight?: boolean; explanation?: string }) {
  return (
    <Stack spacing={0.25} sx={{ minWidth: 0 }} title={props.explanation}>
      <Typography
        sx={{ fontFamily: MONO, fontSize: 10, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: "rgba(196,181,253,0.65)" }}
      >
        {props.label}
      </Typography>
      <Typography noWrap sx={{ fontFamily: MONO, fontSize: { xs: 14, sm: 16 }, fontWeight: 700, color: props.highlight ? "#6EE7B7" : "#FFFFFF" }}>
        {props.value}
      </Typography>
    </Stack>
  );
}

// The flight from liftoff to separation and a little beyond: diagram,
// live readouts and the mission log, all driven by the mission time.
export default function AscentView(props: { missionS: number; orbitAltitudeKm: number; satelliteName: string }) {
  const { missionS, orbitAltitudeKm, satelliteName } = props;
  const separated = missionS >= SEPARATION_MISSION_S;
  const inOrbit = missionS >= ORBIT_MISSION_S;
  const events = ascentEvents(satelliteName).filter((e) => e.missionS <= missionS);

  const STEPS = 120;
  const fullTrack = React.useMemo(() => {
    const points: string[] = [];
    for (let i = 0; i <= STEPS; i++) {
      const p = trackPoint((SEPARATION_MISSION_S * i) / STEPS, orbitAltitudeKm);
      points.push(`${p.x.toFixed(1)},${p.y.toFixed(1)}`);
    }
    return points;
  }, [orbitAltitudeKm]);
  const flown = fullTrack.slice(0, Math.max(1, Math.round((Math.min(missionS, SEPARATION_MISSION_S) / SEPARATION_MISSION_S) * STEPS) + 1));

  const site = pointAt(EARTH_R, START_DEG);
  // The picture stops moving a while after separation (the clock goes on),
  // so a group coming back later still sees its satellite in the frame.
  const drawnS = Math.min(missionS, SEPARATION_MISSION_S + 900);
  const vehicle = trackPoint(drawnS, orbitAltitudeKm);
  const drift = Math.max(0, drawnS - SEPARATION_MISSION_S) * DRIFT_DEG_PER_S;
  const satellite = pointAt(ORBIT_R + Math.min(6, drift * 40), angleAt(drawnS) + drift);
  const firstStage = fallingPoint(missionS, STAGE_SEPARATION_S, orbitAltitudeKm, 4);
  const fairings = [fallingPoint(missionS, FAIRING_S, orbitAltitudeKm, 2.5), fallingPoint(missionS, FAIRING_S, orbitAltitudeKm, 5.5)];

  return (
    <Stack spacing={2}>
      <Box sx={{ borderRadius: 2.5, overflow: "hidden", border: "1px solid rgba(168,85,247,0.3)", bgcolor: "#05030a" }}>
        <Box
          component="svg"
          viewBox="0 0 600 260"
          role="img"
          aria-label={`Flight to orbit, ${formatMissionClock(missionS)}, ${flightStageAt(missionS)}`}
          sx={{ display: "block", width: "100%", height: "auto" }}
        >
          <defs>
            <radialGradient id="ascentEarth" cx="50%" cy="10%" r="60%">
              <stop offset="0%" stopColor="#3B82F6" />
              <stop offset="55%" stopColor="#1E3A8A" />
              <stop offset="100%" stopColor="#0B1330" />
            </radialGradient>
          </defs>
          {STARS.map((s, i) => (
            <circle key={i} cx={s.x} cy={s.y} r={s.r} fill="#FFFFFF" opacity={s.o} />
          ))}
          <circle cx={CX} cy={CY} r={EARTH_R + 10} fill="rgba(96,165,250,0.12)" />
          <circle cx={CX} cy={CY} r={EARTH_R} fill="url(#ascentEarth)" />
          <circle
            cx={CX}
            cy={CY}
            r={ORBIT_R}
            fill="none"
            stroke={inOrbit ? "rgba(110,231,183,0.7)" : "rgba(167,139,250,0.55)"}
            strokeWidth={inOrbit ? 1.6 : 1.2}
            strokeDasharray="5 6"
          />
          <line x1={14} y1={20} x2={38} y2={20} stroke={inOrbit ? "rgba(110,231,183,0.8)" : "rgba(167,139,250,0.8)"} strokeWidth={1.4} strokeDasharray="5 4" />
          <text x={44} y={24} fill="rgba(196,181,253,0.85)" fontSize={11} fontFamily={MONO}>
            target orbit · {Math.round(orbitAltitudeKm)} km
          </text>

          <polyline points={fullTrack.join(" ")} fill="none" stroke="rgba(255,255,255,0.14)" strokeWidth={1.5} strokeDasharray="3 5" />
          <polyline points={flown.join(" ")} fill="none" stroke="#F59E0B" strokeWidth={2.2} strokeLinecap="round" />
          <circle cx={site.x} cy={site.y} r={4} fill="#F59E0B" />
          <text x={site.x - 6} y={site.y + 16} fill="rgba(253,230,138,0.85)" fontSize={10} fontFamily={MONO} textAnchor="middle">
            launch site
          </text>

          {firstStage && (
            <g>
              <circle cx={firstStage.x} cy={firstStage.y} r={3.2} fill="#94A3B8" />
              <text x={firstStage.x + 7} y={firstStage.y + 3} fill="rgba(148,163,184,0.9)" fontSize={9.5} fontFamily={MONO}>
                first stage
              </text>
            </g>
          )}
          {fairings.map((f, i) =>
            f ? <circle key={i} cx={f.x} cy={f.y} r={2} fill="#CBD5E1" opacity={0.85} /> : null
          )}

          {/* The upper stage with the satellite still on board, or after separation both on their own. */}
          <circle cx={vehicle.x} cy={vehicle.y} r={separated ? 3.5 : 5} fill={separated ? "#94A3B8" : "#FDE68A"}>
            {!separated && <animate attributeName="r" values="4;6;4" dur="1s" repeatCount="indefinite" />}
          </circle>
          {!separated && missionS < ORBIT_MISSION_S && (
            <circle cx={vehicle.x} cy={vehicle.y} r={9} fill="none" stroke="rgba(245,158,11,0.45)" strokeWidth={1.5} />
          )}
          {separated && (
            <g>
              <text x={vehicle.x - 8} y={vehicle.y + 16} fill="rgba(148,163,184,0.9)" fontSize={9.5} fontFamily={MONO} textAnchor="end">
                upper stage
              </text>
              <circle cx={satellite.x} cy={satellite.y} r={5} fill="#6EE7B7">
                <animate attributeName="r" values="4;7;4" dur="1.4s" repeatCount="indefinite" />
              </circle>
              <text x={satellite.x} y={satellite.y - 12} fill="#6EE7B7" fontSize={12} fontWeight={700} fontFamily={MONO} textAnchor="middle">
                {satelliteName}
              </text>
            </g>
          )}
        </Box>
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(5, minmax(0, 1fr))" }, gap: 2 }}>
        <Readout label="Mission time" value={formatMissionClock(missionS)} />
        <Readout label="Stage" value={flightStageAt(missionS)} highlight={separated} />
        <Readout label="Altitude" value={`${Math.round(altitudeKmAt(missionS, orbitAltitudeKm))} km`} />
        <Readout label="Velocity" value={`${speedKmSAt(missionS, orbitAltitudeKm).toFixed(2)} km/s`} />
        <Readout
          label="From launch site"
          value={`${Math.round(downrangeKmAt(missionS, orbitAltitudeKm)).toLocaleString("en-US")} km`}
          explanation="Distance over the ground from the launch site to the point below the rocket (mission control calls it downrange). Rockets fly mostly sideways to build up the speed for orbit."
        />
      </Box>

      <Stack
        spacing={0.75}
        sx={{ p: 1.5, borderRadius: 2, bgcolor: "rgba(9,5,11,0.6)", border: "1px solid rgba(168,85,247,0.2)", minHeight: 120 }}
        aria-live="polite"
      >
        {events.map((e) => (
          <Stack key={e.missionS} direction="row" spacing={1.5}>
            <Typography sx={{ fontFamily: MONO, fontSize: 12, color: "#A5B4FC", flexShrink: 0 }}>{formatMissionClock(e.missionS)}</Typography>
            <Typography sx={{ fontFamily: MONO, fontSize: 12, color: e.missionS === SEPARATION_MISSION_S ? "#6EE7B7" : "rgba(226,232,240,0.9)" }}>
              {e.text}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Stack>
  );
}
